// F3 (#809), TIER 3 — stash the device's un-uploaded held work on an OFFLINE
// sign-out, then AUDIT the outcome. Split out of auth-provider.tsx (like the
// queue rescue's preserve-upload-queue-audit.ts) to keep that file under the
// 500-line gate and so the bounded stash + audit is unit-testable without
// rendering the whole provider.
//
// THE LOSS THIS CLOSES. Tiers 1+2 ship held work to the durable server inbox
// BEFORE `disconnectAndClear` wipes it — but that POST needs the network. On an
// OFFLINE sign-out the POST can't reach the server, so the existing rescues audit
// "could NOT preserve / discard" and the clear destroys the un-uploaded counts.
// This is the offline net: it stashes ALL of the device's held work (quarantine +
// ps_crud queue) into durable on-device IndexedDB that SURVIVES the clear, keyed to
// the LOGGING-OUT user, so the on-login re-inject (auto-rescue-held-captures.tsx)
// ships it once the device is back online — and only for that same user.
//
// BEST-EFFORT + BOUNDED, like the quarantine/queue rescues: it NEVER throws and
// NEVER traps the surveyor on a shared device — a wedged worker is audited (NOT
// silent) and the sign-out proceeds. Wired into proceedLogout() on the OFFLINE
// branch (navigator.onLine === false), where the online rescues are skipped
// because their POSTs are doomed.
//
// RESIDUAL (documented, accepted): the offline gate keys on navigator.onLine. A
// dead-zone sign-out where onLine is true but the network is unreachable takes the
// ONLINE branch, whose rescues then fail and audit the discard LOUDLY (never
// silent). Stashing on a failed online ship too would close that, but needs the
// online rescues to report ship-success back; tracked as a follow-up.

import * as Sentry from '@sentry/react'

import {
  heldCaptureTotal,
  stashHeldWorkOnLogout,
  type HeldCaptureLoad,
} from '../hooks/powersync/logout-cleanup'

// Upper bound on the stash (a local SELECT + getCrudBatch read + an IndexedDB
// write). Like the rescue bounds, it only trips for a wedged worker; on timeout we
// audit and proceed, never trapping the surveyor on a shared device.
const STASH_TIMEOUT_MS = 6_000

/**
 * Stash the device's un-uploaded held work for `userId` before the offline clear,
 * then audit. `load` is the pre-clear held probe — used only for the audit copy.
 * `stampedAt` is the device clock at logout (forensic only; passed in so the caller
 * owns the single clock read). Never throws.
 */
export async function stashHeldWorkOnLogoutThenAudit(
  userId: string,
  load: HeldCaptureLoad,
  stampedAt: string,
): Promise<void> {
  // `result` stays null on BOTH the timeout arm and a thrown stash, so
  // `result !== null` precisely means "the stash resolved" (mirrors the rescue
  // audits' `result !== null` convention).
  let result: { stashed: number; truncated: boolean } | null = null
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    result = await Promise.race<{ stashed: number; truncated: boolean } | null>([
      stashHeldWorkOnLogout(userId, stampedAt),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => {
          resolve(null)
        }, STASH_TIMEOUT_MS)
      }),
    ])
  } catch {
    // Best-effort: a failed stash (DB locked / wedged worker) is audited below as
    // an at-risk discard, never swallowed silently.
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }

  const total = heldCaptureTotal(load)
  if (result !== null) {
    // Durably retained on-device — the clear no longer loses these counts; the
    // on-login re-inject ships them when this user is next online.
    Sentry.captureMessage(
      `logout (offline): stashed ${result.stashed} un-uploaded capture count(s) on this ` +
        `device for re-inject on this user's next online login; the clear will not ` +
        `discard them (F3/issue 809 Tier-3)`,
      'info',
    )
    if (result.truncated) {
      // The pending queue exceeded the read ceiling: the stashed counts are only the
      // captured first page, and the clear that follows wipes the unread overflow.
      // Surface the at-risk overflow loudly so it is never a silent loss (Codex :316).
      Sentry.captureMessage(
        `logout (offline): the device upload queue exceeded the capture ceiling — the ` +
          `stashed ${result.stashed} count(s) are the captured subset; an unread overflow ` +
          `may be discarded by the clear (F3/issue 809 Tier-3; Codex :316)`,
        'warning',
      )
    }
  } else {
    // The stash failed/timed out — the clear WILL discard the held counts. Audited
    // at warning so Sentry severity filtering surfaces the loss; never silent.
    Sentry.captureMessage(
      `logout (offline): could NOT stash device-held capture counts before ` +
        `clearing (held total ${total ?? 'indeterminate'}); signing out anyway — ` +
        `held counts will be discarded from this device (F3/issue 809 Tier-3)`,
      'warning',
    )
  }
}
